import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import MessageItem from './MessageItem';
import SkeletonLoader from '../common/SkeletonLoader';
import messagesService from '../../api/messages';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useUniversity } from '../../contexts/UniversityContext';
import { FaUniversity } from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';

// Animación de aparición suave para contenedores
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animación para el filtro de hashtags
const fadeInSlide = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  margin-bottom: 20px;
  position: relative;
  z-index: 0; /* Asegurarse que esté por debajo del MessageComposer */
  
  @media (max-width: 768px) {
    margin-bottom: 15px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 10px;
  }
`;

const FeedContainer = styled.div`
  background: ${props => props.theme.card};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  overflow: hidden;
  transition: opacity 0.2s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 500px;
  height: 100%;
  opacity: 1;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    min-height: 400px;
  }

  @media (max-width: 480px) {
    min-height: 300px;
  }

  @media (max-height: 600px) {
    min-height: auto;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${props => props.theme.divider};
  
  @media (max-width: 480px) {
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Tab = styled.button.attrs(props => ({
  type: 'button',
}))`
  padding: 15px;
  font-size: 15px;
  border: none;
  background: none;
  cursor: pointer;
  font-weight: ${props => props.$active ? '600' : '400'};
  color: ${props => props.$active ? props.theme.primary : props.theme.textSecondary};
  position: relative;
  min-width: 80px;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 12px;
    font-size: 14px;
    min-width: 70px;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
    font-size: 13px;
    min-width: auto;
    flex: 1;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background-color: ${props => props.$active ? props.theme.primary : 'transparent'};
    border-radius: 3px 3px 0 0;
  }
`;

// Contenedor con espacio reservado para los filtros
const FilterSpace = styled.div`
  height: ${props => (props.$visible ? 'auto' : '0')};
  overflow: hidden;
  transition: height 0.4s ease-in-out;
  opacity: ${props => (props.$visible ? 1 : 0)};
`;

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
  padding: 12px 15px;
  background-color: ${props => props.theme.backgroundLight};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  animation: ${slideIn} 0.4s ease-out forwards;
  min-height: 60px; /* Altura mínima para los filtros */
  
  @media (max-width: 768px) {
    padding: 10px;
    margin-bottom: 12px;
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    margin-bottom: 10px;
    gap: 6px;
  }
`;

const FilterButton = styled.button`
  background-color: ${props => props.$active ? props.theme.primary : props.theme.buttonLight};
  color: ${props => props.$active ? 'white' : props.theme.textSecondary};
  border: none;
  border-radius: 50px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-grow: 1;
  justify-content: center;
  max-width: 150px;
  box-shadow: ${props => props.$active ? '0 3px 8px rgba(0, 0, 0, 0.15)' : 'none'};
  transform: ${props => props.$active ? 'translateY(-1px)' : 'none'};
  opacity: 0;
  animation: ${fadeIn} 0.3s ease-out forwards;
  animation-delay: ${props => props.$index * 0.08}s;
  
  &:hover {
    background-color: ${props => props.$active ? props.theme.primaryHover : props.theme.buttonLightHover};
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 7px 14px;
    font-size: 13px;
  }
  
  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 12px;
    max-width: none;
    flex-basis: calc(50% - 6px);
  }
`;

const FilterIcon = styled.span`
  font-size: 16px;
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

// Espacio reservado para el filtro de hashtags
const HashtagFilterSpace = styled.div`
  height: ${props => (props.$visible ? 'auto' : '0')};
  overflow: hidden;
  transition: height 0.4s ease-in-out;
  opacity: ${props => (props.$visible ? 1 : 0)};
`;

const HashtagFilter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 15px;
  background-color: ${props => props.theme.backgroundLight};
  border-bottom: 1px solid ${props => props.theme.divider};
  margin-bottom: 5px;
  animation: ${fadeInSlide} 0.3s ease-out forwards;
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 10px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const HashtagBadge = styled.div`
  background-color: ${props => props.theme.primary}; 
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-left: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.3s ease-out forwards;
  animation-delay: 0.1s;
  opacity: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  
  @media (max-width: 768px) {
    margin-left: 8px;
    max-width: 180px;
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
    padding: 5px 10px;
    margin-left: 8px;
    max-width: 200px;
    align-self: flex-start;
  }
  
  @media (max-width: 360px) {
    max-width: 160px;
  }
`;

const HashtagFilterInfo = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  animation: ${fadeIn} 0.3s ease-out forwards;
  animation-delay: 0.05s;
  opacity: 0;
  
  @media (max-width: 480px) {
    margin-left: 0;
    width: 100%;
    margin-top: 4px;
  }
`;

const HashtagFilterLabel = styled.span`
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
  margin-right: 8px;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
  }
`;

const ClearFilter = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.3s ease-out forwards;
  opacity: 0;
  white-space: nowrap;
  min-width: 120px;
  
  @media (max-width: 768px) {
    font-size: 13px;
    padding: 5px 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
    padding: 5px 12px;
    width: 100%;
    justify-content: center;
  }
  
  &:hover {
    background-color: ${props => props.theme.primaryHover};
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

// Replace the text icon with an SVG icon
const BackArrowIcon = styled.svg`
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const MessagesContainer = styled.div`
  padding: 10px 0;
  background-color: ${props => props.theme.card};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 300px;
  transition: opacity 0.2s ease;
  opacity: ${props => props.$loading ? 0.6 : 1};
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    min-height: 250px;
  }

  @media (max-width: 480px) {
    min-height: 200px;
  }

  @media (max-height: 600px) {
    min-height: auto;
    padding: 5px 0;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 30px 20px;
  color: ${props => props.theme.textSecondary};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$loading ? 0.6 : 1};
  transition: opacity 0.3s ease;
  animation: ${fadeIn} 0.5s ease;
  min-height: 300px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 25px 15px;
    min-height: 250px;
  }
  
  @media (max-width: 480px) {
    padding: 20px 10px;
    font-size: 14px;
    min-height: 200px;
  }

  @media (max-height: 600px) {
    padding: 15px 10px;
    min-height: auto;
  }
`;

const FrogIcon = styled.div`
  font-size: 80px;
  margin-bottom: 20px;
  animation: bounce 2s infinite;
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
    40% {transform: translateY(-20px);}
    60% {transform: translateY(-10px);}
  }
  
  @media (max-width: 768px) {
    font-size: 70px;
  }
  
  @media (max-width: 480px) {
    font-size: 60px;
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  color: ${props => props.theme.text};
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const EmptyStateMessage = styled.p`
  font-size: 16px;
  line-height: 1.5;
  max-width: 400px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

// Nuevo componente para mensaje específico sin universidad
const NoUniversityState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: ${props => props.theme.textSecondary};
  
  @media (max-width: 768px) {
    padding: 30px 15px;
  }
  
  @media (max-width: 480px) {
    padding: 25px 10px;
  }
`;

const IconContainer = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
  color: ${props => props.theme.primary};
  opacity: 0.7;
  
  @media (max-width: 768px) {
    font-size: 40px;
    margin-bottom: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 36px;
    margin-bottom: 12px;
  }
`;

const MessageTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 10px;
  color: ${props => props.theme.text};
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const MessageText = styled.p`
  margin-bottom: 0;
  font-size: 16px;
  line-height: 1.5;
  max-width: 500px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  width: 100%;
  flex-grow: 1;
  background-color: ${props => props.theme.card};
  opacity: 0.8;
  transition: opacity 0.2s ease;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;

  &:after {
    content: "";
    width: 32px;
    height: 32px;
    border: 2px solid ${props => props.theme.divider};
    border-top: 2px solid ${props => props.theme.primary};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @media (max-width: 768px) {
    min-height: 350px;
    &:after {
      width: 24px;
      height: 24px;
    }
  }
  
  @media (max-width: 480px) {
    min-height: 300px;
  }
`;

// Renombrar el componente para evitar conflictos
const AnimatedItem = styled.div`
  transition: opacity 0.15s ease;
  opacity: 1;
`;

const MessageList = ({ hashtag, onHashtagClick, onClearHashtag }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [lastMessageId, setLastMessageId] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [feedType, setFeedType] = useState('recent');
  const [timePeriod, setTimePeriod] = useState('day');
  const [error, setError] = useState(null);
  
  // Set para mantener IDs de mensajes procesados durante toda la vida del componente
  const processedIds = useRef(new Set());
  
  // Referencias para evitar recreaciones de funciones
  const feedTypeRef = useRef(feedType);
  const pageRef = useRef(page);
  const loadingRef = useRef(loading);
  const loadingMoreRef = useRef(loadingMore);
  const hasMoreRef = useRef(hasMore);
  const lastMessageIdRef = useRef(lastMessageId);
  const timePeriodRef = useRef(timePeriod);
  
  // Referencia al elemento observado para infinite scroll
  const observerRef = useRef(null);
  const observerTarget = useRef(null);
  
  const { addMessageHandler } = useWebSocket();
  const { selectedUniversity, currentUniversity } = useUniversity();
  const { isAuthenticated, user } = useAuth();
  
  // Actualizar refs cuando cambian los valores
  useEffect(() => {
    feedTypeRef.current = feedType;
    pageRef.current = page;
    loadingRef.current = loading;
    loadingMoreRef.current = loadingMore;
    hasMoreRef.current = hasMore;
    lastMessageIdRef.current = lastMessageId;
    timePeriodRef.current = timePeriod;
  }, [feedType, page, loading, loadingMore, hasMore, lastMessageId, timePeriod]);
  
  // Cargar mensajes, versión estable que no causa bucles infinitos
  const loadMessages = useCallback(async (reset = false) => {
    // No cargar mensajes si no hay universidad seleccionada
    if (!currentUniversity) {
      setLoading(false);
      return;
    }
    
    // Evitar cargar si ya estamos cargando
    if ((loadingMoreRef.current || loadingRef.current) && !reset) {
      return;
    }
    
    // Establecer estados de carga
    if (reset) {
      setMessages([]);
      setLoading(true);
      setPage(1);
      setLastMessageId(null);
      setHasMore(true);
      setError(null); // Resetear cualquier error previo
    } else {
      setLoadingMore(true);
      loadingMoreRef.current = true;
    }
    
    try {
      let response;
      const currentPage = reset ? 1 : pageRef.current;
      const currentPeriod = timePeriodRef.current;
      
      if (hashtag) {
        response = await messagesService.getMessagesByHashtag(
          hashtag, 
          currentPage,
          10,
          selectedUniversity
        );
      } else if (feedTypeRef.current === 'recent') {
        // Solo incluir lastMessageId si no es un reset y existe
        const params = {
          page: currentPage,
          limit: 10,
          university: selectedUniversity
        };
        if (!reset && lastMessageIdRef.current) {
          params.last_id = lastMessageIdRef.current;
        }
        response = await messagesService.getRecentMessages(
          params.page,
          params.limit,
          params.last_id,
          params.university
        );
      } else if (feedTypeRef.current === 'popular') {
        response = await messagesService.getPopularMessages(
          currentPeriod,
          currentPage,
          10,
          selectedUniversity
        );
      }
      
      // Verificar que response es un array válido
      if (!response || !Array.isArray(response)) {
        console.error("Invalid response format:", response);
        setHasMore(false);
        hasMoreRef.current = false;
        setError("Formato de respuesta inválido");
        return;
      }
      
      if (response.length > 0) {
        const formattedMessages = response.map(msg => {
          // Si el mensaje ya tiene el formato correcto, devolverlo tal cual
          if (msg.author) return msg;
          
          // Transformar el mensaje con los datos del servidor
          return {
            id: msg.id || msg._id,
            text: msg.text,
            image: msg.image,
            author: {
              username: msg.username
            },
            author_id: msg.user_id,
            created_at: msg.timestamp || msg.created_at,
            likes_count: msg.likes || msg.likes_count || 0,
            comments_count: msg.comments || msg.comments_count || 0,
            shares_count: msg.shares || msg.shares_count || 0,
            liked: msg.liked || false,
            shared: msg.shared || false,
            hashtags: msg.hashtags || []
          };
        });
        
        // Verificar el estado de like para cada mensaje si el usuario está autenticado
        let messagesWithLikeStatus = formattedMessages;
        
        if (isAuthenticated && user) {
          // Procesar mensajes y verificar likes en paralelo
          messagesWithLikeStatus = await Promise.all(formattedMessages.map(async (msg) => {
            // Verificar el estado de like para este mensaje
            let likeStatus = { liked: msg.liked || false };
            try {
              likeStatus = await messagesService.checkLikeStatus(msg.id);
            } catch (error) {
              console.error(`Error checking like status for message ${msg.id}:`, error);
            }
            
            return {
              ...msg,
              liked: likeStatus.liked
            };
          }));
        }
        
        setMessages(prev => {
          // Usar un Set para deduplicación
          const messageIds = new Set(reset ? [] : prev.map(m => m.id));
          const uniqueMessages = messagesWithLikeStatus.filter(msg => !messageIds.has(msg.id));
          
          // Solo actualizar lastMessageId si tenemos mensajes
          if (uniqueMessages.length > 0 && messagesWithLikeStatus.length > 0) {
            const lastMsg = messagesWithLikeStatus[messagesWithLikeStatus.length - 1];
            setLastMessageId(lastMsg.id);
            lastMessageIdRef.current = lastMsg.id;
          }
          
          // Actualizar page y hasMore
          setHasMore(uniqueMessages.length === 10);
          hasMoreRef.current = uniqueMessages.length === 10;
          
          if (reset) {
            setPage(2); // Establecer a 2 después de reset
            pageRef.current = 2;
          } else if (uniqueMessages.length > 0) {
            setPage(p => {
              const newPage = p + 1;
              pageRef.current = newPage;
              return newPage;
            });
          }
          
          return reset ? uniqueMessages : [...prev, ...uniqueMessages];
        });
      } else {
        setHasMore(false);
        hasMoreRef.current = false;
      }
    } catch (error) {
      console.error('Error al cargar mensajes:', error);
      setError('No se pudieron cargar los mensajes'); // Usar setError
    } finally {
      setLoading(false);
      loadingRef.current = false;
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [hashtag, selectedUniversity, isAuthenticated, user, currentUniversity]);
  
  // Cargar mensajes iniciales - Modificar el useEffect
  useEffect(() => {
    if (currentUniversity) {  // Solo cargar si hay una universidad seleccionada
      loadMessages(true);
    } else {
      // Asegurarse de que no se muestra el spinner cuando no hay universidad
      setLoading(false);
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedType, hashtag, timePeriod, currentUniversity]);
  
  // Configurar Intersection Observer para scroll infinito
  useEffect(() => {
    // Limpiar el observer anterior si existe
    if (observerRef.current && observerTarget.current) {
      observerRef.current.unobserve(observerTarget.current);
    }
    
    // Crear un nuevo observer con un debounce para evitar múltiples llamadas
    let debounceTimeout = null;
    
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMoreRef.current && !loadingMoreRef.current && !loadingRef.current) {
          // Cancelar cualquier timeout previo para evitar múltiples cargas
          if (debounceTimeout) clearTimeout(debounceTimeout);
          
          // Usar debounce para evitar múltiples llamadas rápidas
          debounceTimeout = setTimeout(() => {
            loadMessages(false);
          }, 300); // 300ms debounce
        }
      },
      { threshold: 0.1 } // Detectar cuando al menos 10% del elemento es visible
    );
    
    // Observar el elemento target
    if (observerTarget.current) {
      observerRef.current.observe(observerTarget.current);
    }
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [loadMessages, messages.length]); // Redefinir cuando cambie la lista de mensajes
  
  // Escuchar mensajes nuevos por WebSocket
  useEffect(() => {
    const handleWebSocketMessage = (data) => {
      // Handle like updates
      if (data.type === 'like_update' && data.message) {
        // Invalidar caché de mensajes populares
        if (typeof messagesService.invalidateMessageCache === 'function') {
          // Solo invalidar caché de populares cuando hay actualizaciones de likes
          messagesService.clearByPrefix ? 
            messagesService.clearByPrefix('popular:') : 
            messagesService.invalidateMessageCache();
        }
        
        setMessages(prevMessages => {
          return prevMessages.map(msg => {
            if (msg.id === data.message.id) {
              // Update message with server data
              return {
                ...msg,
                likes_count: data.message.likes_count || data.message.likes || msg.likes_count,
                liked: data.message.liked !== undefined ? data.message.liked : msg.liked
              };
            }
            return msg;
          });
        });
        return;
      }
      
      // Only process message type or new message
      if (data.type === 'message' || data.type === 'new_message') {
        // Invalidar caché cuando hay nuevos mensajes
        if (typeof messagesService.invalidateMessageCache === 'function') {
          messagesService.invalidateMessageCache();
        }
        
        // Extract message data according to format
        const messageData = data.message || data;
        
        // If message has no ID or is empty, ignore it
        if (!messageData.id || (messageData.text === "" && !messageData.image)) {
          return;
        }
        
        // Check if it comes from a temporary anonymous user
        if (messageData.user_id && messageData.user_id.startsWith("anon_")) {
          return;
        }
        
        // Mark as processed
        processedIds.current.add(messageData.id);

        // IMPORTANT: Filter by university - only add messages from the current university
        if (messageData.university && currentUniversity && messageData.university !== currentUniversity.id) {
          return; // Skip messages from other universities
        }
        
        // Format message to match our expected format
        const formattedMessage = {
          id: messageData.id,
          text: messageData.text || "", // Ensure text is not undefined
          author: {
            username: messageData.username || messageData.author?.username || "Usuario"
          },
          author_id: messageData.user_id || messageData.author_id,
          created_at: messageData.created_at || messageData.timestamp || new Date().toISOString(),
          likes_count: messageData.likes || messageData.likes_count || 0,
          comments_count: messageData.comments || messageData.comments_count || 0,
          shares_count: messageData.shares || messageData.shares_count || 0,
          image: messageData.image || null,
          liked: messageData.liked || false,
          shared: messageData.shared || false,
          hashtags: messageData.hashtags || [],
          university: messageData.university || currentUniversity?.id // Save university for future filtering
        };
        
        // Add message to list
        setMessages(prevMessages => {
          // Don't duplicate messages
          if (prevMessages.some(m => m.id === formattedMessage.id)) {
            return prevMessages;
          }
          return [formattedMessage, ...prevMessages];
        });
      }
    };
    
    // Register handler
    const unsubscribe = addMessageHandler(handleWebSocketMessage);
    
    return () => {
      unsubscribe();
    };
  }, [addMessageHandler, currentUniversity]); // Add currentUniversity as a dependency
  
  // Manejar click en un hashtag
  const handleHashtagClick = (tag) => {
    if (onHashtagClick) {
      onHashtagClick(tag);
    }
  };
  
  // Limpiar el filtro de hashtag
  const clearHashtagFilter = () => {
    if (onClearHashtag) {
      onClearHashtag();
    }
  };
  
  // Cambiar el tipo de feed
  const changeFeedType = (type) => {
    if (feedType !== type) {
      setFeedType(type);
      setPage(1);
      setLastMessageId(null);
      
      // Reset time period to day when switching to popular feed
      if (type === 'popular') {
        setTimePeriod('day');
      }
      
      // Reset will happen through the useEffect dependency on feedType
    }
  };
  
  // Cambiar el período de tiempo para populares
  const changeTimePeriod = (period) => {
    if (timePeriod !== period) {
      setTimePeriod(period);
      timePeriodRef.current = period;
      
      // Explicitly reload messages with the new period
      if (feedType === 'popular') {
        setPage(1);
        setLastMessageId(null);
        // No llamar a loadMessages(true) aquí - se disparará automáticamente por el efecto
      }
    }
  };
  
  // Handle like updates from MessageItem component
  const handleMessageLikeUpdate = useCallback((messageId, liked, likesCount) => {
    setMessages(prevMessages => {
      return prevMessages.map(msg => {
        if (msg.id === messageId) {
          return {
            ...msg,
            liked: liked,
            likes_count: likesCount
          };
        }
        return msg;
      });
    });
  }, []);
  
  // Efecto de limpieza cuando el componente se desmonta
  useEffect(() => {
    const cleanup = () => {
      // Limpiar observadores y cualquier estado pendiente
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      setLoading(false);
      setLoadingMore(false);
    };
    
    return cleanup;
  }, []);
  
  return (
    <Container>
      <FeedContainer>
        {!hashtag && (
          <TabsContainer>
            <Tab 
              $active={feedType === 'recent'} 
              onClick={() => changeFeedType('recent')}
            >
              Recientes
            </Tab>
            <Tab 
              $active={feedType === 'popular'} 
              onClick={() => changeFeedType('popular')}
            >
              Populares
            </Tab>
          </TabsContainer>
        )}
        
        {/* Espacio reservado para filtros que está siempre presente pero solo visible cuando es necesario */}
        <FilterSpace $visible={feedType === 'popular' && !hashtag}>
          {feedType === 'popular' && !hashtag && (
            <FilterContainer>
              <FilterButton 
                $active={timePeriod === 'day'} 
                onClick={() => changeTimePeriod('day')}
                $index={0}
              >
                <FilterIcon>🔥</FilterIcon>
                Hoy
              </FilterButton>
              <FilterButton 
                $active={timePeriod === 'week'} 
                onClick={() => changeTimePeriod('week')}
                $index={1}
              >
                <FilterIcon>📅</FilterIcon>
                Esta semana
              </FilterButton>
              <FilterButton 
                $active={timePeriod === 'month'} 
                onClick={() => changeTimePeriod('month')}
                $index={2}
              >
                <FilterIcon>📆</FilterIcon>
                Este mes
              </FilterButton>
              <FilterButton 
                $active={timePeriod === 'all'} 
                onClick={() => changeTimePeriod('all')}
                $index={3}
              >
                <FilterIcon>🏆</FilterIcon>
                Todo el tiempo
              </FilterButton>
            </FilterContainer>
          )}
        </FilterSpace>
        
        {/* Espacio reservado para el filtro de hashtags */}
        <HashtagFilterSpace $visible={!!hashtag}>
          {hashtag && (
            <HashtagFilter>
              <ClearFilter onClick={clearHashtagFilter}>
                <BackArrowIcon viewBox="0 0 24 24">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </BackArrowIcon>
                Volver al feed
              </ClearFilter>
              <HashtagFilterInfo>
                <HashtagFilterLabel>Mostrando:</HashtagFilterLabel>
                <HashtagBadge>#{hashtag}</HashtagBadge>
              </HashtagFilterInfo>
            </HashtagFilter>
          )}
        </HashtagFilterSpace>
        
        <MessagesContainer>
          {loading && currentUniversity && <LoadingSpinner />}
          
          {error && (
            <EmptyState>
              {error}
            </EmptyState>
          )}
          
          {!currentUniversity ? (
            <NoUniversityState>
              <IconContainer>
                <FaUniversity />
              </IconContainer>
              <MessageTitle>No hay universidad seleccionada</MessageTitle>
              <MessageText>
                Selecciona una universidad para ver el feed de mensajes correspondiente.
              </MessageText>
            </NoUniversityState>
          ) : !loading && messages.length > 0 ? (
            <>
              {messages.map((message, index) => (
                <AnimatedItem key={message.id}>
                  <MessageItem 
                    message={message} 
                    onHashtagClick={handleHashtagClick} 
                    onLikeUpdate={handleMessageLikeUpdate}
                  />
                </AnimatedItem>
              ))}
              
              {/* Elemento observado para infinite scroll */}
              <div ref={observerTarget}>
                {hasMore && loadingMore && (
                  <SkeletonLoader type="message" count={2} />
                )}
              </div>
            </>
          ) : !loading && (
            <EmptyState>
              <FrogIcon>🐸</FrogIcon>
              <EmptyStateTitle>¡Sé el primero en compartir! 🎭</EmptyStateTitle>
              <EmptyStateMessage>
                Este es tu espacio para compartir de forma 100% anónima. ¿Qué te gustaría decir sin que nadie sepa quién eres?
              </EmptyStateMessage>
            </EmptyState>
          )}
        </MessagesContainer>
      </FeedContainer>
    </Container>
  );
};

// Asignar un displayName para facilitar la depuración
MessageList.displayName = 'MessageList';

export default React.memo(MessageList); // Usar memo para evitar rerenderizados innecesarios
